import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useBrand } from "../../context/BrandContext";
import {
  fetchTeChronoRecords,
  saveTeChronoRecord,
  deleteTeChronoRecord,
} from "../../api/coachChronoApi";
import { formatChronoClock } from "../../utils/coachChronoPlayers";
import {
  getStationCameraTarget,
  normalizeStationCameraTarget,
  resolveStationCameraKey,
  setStationCameraTarget,
  stationCameraTargetKey,
  stationCameraTargetLabel,
} from "../../utils/stationCameraBinding";
import {
  buildChronoCameraOptions,
  normalizeCameraPhones,
} from "../../utils/chronoCameraOptions";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { useChronoSessionVideo } from "../../hooks/useChronoSessionVideo";
import { ReviewVideoFullscreenPlayer } from "../../components/ReviewVideoFullscreenPlayer";
import { resolveSignedMediaUrl, peekSignedMediaUrl } from "../../api/signedMediaUrl";
import "./LiveTrainingCoachChronoPanel.css";

function emptyTimerState() {
  return {
    running: false,
    startedAt: null,
    baseMs: 0,
    displaySec: 0,
    savedSec: null,
    dirty: false,
    saving: false,
    deleting: false,
    error: null,
    videoStartMs: null,
    videoEndMs: null,
    videoUrl: null,
    videoMessageId: null,
    videoSessionId: null,
    startedAtWall: null,
    stoppedAtWall: null,
    videoEpochAt: null,
  };
}

function applyVideoMetaFromRecord(cur, video) {
  if (!video) return cur;
  return {
    ...cur,
    videoUrl: video.url || cur.videoUrl,
    videoMessageId: video.message_id || cur.videoMessageId,
    videoStartMs:
      video.start_ms != null ? video.start_ms : cur.videoStartMs,
    videoEndMs: video.end_ms != null ? video.end_ms : cur.videoEndMs,
    startedAtWall: video.started_at || cur.startedAtWall,
    stoppedAtWall: video.stopped_at || cur.stoppedAtWall,
    videoEpochAt: video.video_epoch_at || cur.videoEpochAt,
  };
}

function ChronoInfoButton({ id, text, openId, onToggle, label }) {
  if (!text) return null;
  const open = openId === id;
  return (
    <span className="lt-chrono-panel__info">
      <button
        type="button"
        className="lt-chrono-panel__info-btn"
        aria-expanded={open}
        aria-label={label}
        onClick={() => onToggle(open ? null : id)}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </button>
      {open ? (
        <span className="lt-chrono-panel__info-pop" role="note">
          {text}
        </span>
      ) : null}
    </span>
  );
}

/** Isolated clock — avoids re-rendering the whole panel every animation frame. */
function ChronoClockDisplay({ running, startedAt, baseMs, displaySec }) {
  const [sec, setSec] = useState(displaySec);
  useEffect(() => {
    if (!running || startedAt == null) {
      setSec(displaySec);
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      setSec((baseMs + (Date.now() - startedAt)) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, startedAt, baseMs, displaySec]);

  return (
    <div
      className="lt-chrono-panel__clock"
      aria-live={running ? "polite" : "off"}
    >
      {formatChronoClock(sec)}
    </div>
  );
}

/**
 * Coach Record chrono mode panel — independent per-player stopwatches
 * in Live Training subgroup display order (seated tables, then present roster).
 * Optional continuous video maps each player's start/stop onto a shared clip.
 */
export function LiveTrainingCoachChronoPanel({
  open,
  onClose,
  teId,
  players = [],
  onOpenPlayerPresence = null,
  showPlayerPresenceShortcut = false,
  playerPresenceDisabled = false,
  playerPresenceDisabledTitle = "",
  tsgId = null,
  grId = null,
  tsId = null,
  socket = null,
  coachUserId = null,
  stations = [],
  stationCameraTarget = null,
  /** Timer-only Live Training blocks: Record hits every session camera phone. */
  captureAllCameras = false,
}) {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";

  const stationKey = useMemo(
    () => resolveStationCameraKey({ tsgId, grId, teId }),
    [tsgId, grId, teId]
  );

  const [timers, setTimers] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewClip, setReviewClip] = useState(null);
  const [reviewUrl, setReviewUrl] = useState(null);
  const [cameraPhones, setCameraPhones] = useState([]);
  const [selectedCameraKeys, setSelectedCameraKeys] = useState(() => new Set());
  const [selectAllCameras, setSelectAllCameras] = useState(false);
  const [infoOpenId, setInfoOpenId] = useState(null);
  const selectionTouchedRef = useRef(false);
  const playersRef = useRef(players);
  const timersRef = useRef({});
  const hasLoadedRef = useRef(false);
  const sessionVideo = useChronoSessionVideo({
    teId,
    tsId,
    coachUserId,
    socket,
    stationKey,
    captureAllCameras,
  });
  const broadcastRef = useRef(() => {});

  const stationName = useMemo(() => {
    if (tsgId == null) return null;
    const st = (stations || []).find(
      (s) => String(s?.tsg_id) === String(tsgId)
    );
    if (!st) return String(tsgId);
    return st.is_main
      ? t("mainGroup", "Main group")
      : st.name || String(st.tsg_id);
  }, [stations, tsgId, t]);

  const preferredStationCamera = useMemo(() => {
    const live = normalizeStationCameraTarget(stationCameraTarget);
    if (live) return live;
    return getStationCameraTarget(stationKey);
  }, [stationCameraTarget, stationKey]);

  const cameraOptions = useMemo(
    () =>
      buildChronoCameraOptions({
        players,
        cameraPhones,
        coachUserId,
        preferredStationCamera,
        tsgId,
        stationName,
        t,
      }),
    [
      players,
      cameraPhones,
      coachUserId,
      preferredStationCamera,
      tsgId,
      stationName,
      t,
    ]
  );

  const selectedCameraTargets = useMemo(() => {
    if (selectAllCameras) return cameraOptions;
    return cameraOptions.filter((opt) =>
      selectedCameraKeys.has(stationCameraTargetKey(opt))
    );
  }, [selectAllCameras, cameraOptions, selectedCameraKeys]);

  useEffect(() => {
    sessionVideo.setRecordingSelection?.({
      targets: selectedCameraTargets,
      captureAll: selectAllCameras,
    });
  }, [
    sessionVideo.setRecordingSelection,
    selectedCameraTargets,
    selectAllCameras,
  ]);

  useEffect(() => {
    if (!infoOpenId) return undefined;
    const onPointer = (event) => {
      if (event.target?.closest?.(".lt-chrono-panel__info")) return;
      setInfoOpenId(null);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [infoOpenId]);

  useEffect(() => {
    if (!open || !socket || !tsId) return undefined;
    const onList = (payload = {}) => {
      if (payload.ts_id != null && String(payload.ts_id) !== String(tsId)) {
        return;
      }
      setCameraPhones(normalizeCameraPhones(payload.phones));
    };
    const refresh = () => {
      socket.emit("getSessionCameraPhones", { ts_id: tsId });
    };
    socket.on("sessionCameraPhones", onList);
    socket.on("playerLeft", refresh);
    socket.on("connect", refresh);
    refresh();
    return () => {
      socket.off("sessionCameraPhones", onList);
      socket.off("playerLeft", refresh);
      socket.off("connect", refresh);
    };
  }, [open, socket, tsId]);

  useEffect(() => {
    if (!open) {
      selectionTouchedRef.current = false;
      setSelectAllCameras(false);
      setSelectedCameraKeys(new Set());
      setInfoOpenId(null);
      return;
    }
    if (selectionTouchedRef.current || !preferredStationCamera) return;
    const key = stationCameraTargetKey(preferredStationCamera);
    if (!key) return;
    setSelectedCameraKeys((prev) => {
      if (prev.has(key) && prev.size === 1) return prev;
      return new Set([key]);
    });
  }, [open, preferredStationCamera]);

  playersRef.current = players;
  timersRef.current = timers;

  const playerKeys = useMemo(
    () => (players || []).map((p) => p.key).join("|"),
    [players]
  );

  useEffect(() => {
    if (!open) {
      hasLoadedRef.current = false;
      return;
    }
    setTimers((prev) => {
      const next = {};
      for (const p of playersRef.current || []) {
        next[p.key] = prev[p.key] || emptyTimerState();
      }
      return next;
    });
  }, [open, playerKeys]);

  useEffect(() => {
    if (!open || !teId) return undefined;
    const ac = new AbortController();
    const initial = !hasLoadedRef.current;
    if (initial) setLoading(true);
    setLoadError(null);
    fetchTeChronoRecords(teId, { signal: ac.signal })
      .then((data) => {
        const byUser = new Map();
        for (const rec of data?.records || []) {
          if (rec?.user_id != null) {
            byUser.set(String(rec.user_id), rec);
          }
        }
        setTimers((prev) => {
          const next = { ...prev };
          for (const p of playersRef.current || []) {
            const rec = (p.userId && byUser.get(String(p.userId))) || null;
            const saved =
              rec?.metric_value != null ? Number(rec.metric_value) : null;
            const video = rec?.video || null;
            const cur = next[p.key] || emptyTimerState();
            if (cur.running || cur.dirty) {
              next[p.key] = {
                ...cur,
                savedSec:
                  saved != null && Number.isFinite(saved)
                    ? saved
                    : cur.savedSec,
              };
              continue;
            }
            next[p.key] = applyVideoMetaFromRecord(
              {
                ...cur,
                savedSec:
                  saved != null && Number.isFinite(saved) ? saved : null,
                displaySec:
                  saved != null && Number.isFinite(saved)
                    ? saved
                    : cur.displaySec,
                baseMs:
                  saved != null && Number.isFinite(saved)
                    ? Math.round(saved * 1000)
                    : cur.baseMs,
                dirty: false,
              },
              video
            );
          }
          return next;
        });
        hasLoadedRef.current = true;
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setLoadError(err?.message || t("error") || "Error");
      })
      .finally(() => {
        if (initial) setLoading(false);
      });
    return () => ac.abort();
  }, [open, teId, playerKeys, t]);

  useEffect(() => {
    if (!reviewClip?.url) {
      setReviewUrl(null);
      return undefined;
    }
    let cancelled = false;
    const raw = reviewClip.url;
    const cached = peekSignedMediaUrl(raw);
    setReviewUrl(cached || raw);
    if (cached) return undefined;
    resolveSignedMediaUrl(raw)
      .then((signed) => {
        if (!cancelled && signed) setReviewUrl(signed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [reviewClip]);

  const persistRecord = useCallback(
    async (player, timeSec, videoMeta = {}) => {
      return saveTeChronoRecord(teId, {
        userId: player.userId,
        playerId: player.playerId,
        timeSec,
        videoUrl: videoMeta.videoUrl ?? null,
        videoMessageId: videoMeta.videoMessageId ?? null,
        videoStartMs: videoMeta.videoStartMs ?? null,
        videoEndMs: videoMeta.videoEndMs ?? null,
        startedAt: videoMeta.startedAtWall ?? null,
        stoppedAt: videoMeta.stoppedAtWall ?? null,
        videoEpochAt: videoMeta.videoEpochAt ?? null,
      });
    },
    [teId]
  );

  const broadcastChrono = useCallback(
    (snapshot, videoRecordingOverride) => {
      if (!socket || !tsId || !teId) return;
      const rows = playersRef.current || [];
      const videoRecording =
        videoRecordingOverride != null
          ? Boolean(videoRecordingOverride)
          : Boolean(sessionVideo.recording);
      socket.emit("offTableChronoSync", {
        ts_id: tsId,
        te_id: teId,
        tsg_id: tsgId || null,
        videoRecording,
        players: rows.map((player) => {
          const row = snapshot?.[player.key] || emptyTimerState();
          const liveSec = row.running && row.startedAt != null
            ? (row.baseMs + (Date.now() - row.startedAt)) / 1000
            : Number(row.displaySec) || 0;
          const running = Boolean(row.running);
          return {
            key: player.key,
            name: player.name,
            userId: player.userId || null,
            playerId: player.playerId || null,
            running,
            recording: running && videoRecording,
            startedAtWall: row.startedAtWall || null,
            baseMs: row.baseMs || 0,
            displaySec: liveSec,
            savedSec: row.savedSec != null ? Number(row.savedSec) : null,
            videoStartMs: row.videoStartMs,
            videoEndMs: row.videoEndMs,
            hasVideo: Boolean(row.videoUrl),
          };
        }),
      });
    },
    [socket, tsId, teId, tsgId, sessionVideo.recording]
  );
  broadcastRef.current = broadcastChrono;

  useEffect(() => {
    if (!open) return;
    if (!(playersRef.current || []).length) return;
    broadcastRef.current(timersRef.current);
  }, [open, playerKeys, teId, sessionVideo.recording]);

  /* Keep late-joining cameras / player phones in sync while clocks run. */
  useEffect(() => {
    if (!open) return undefined;
    const anyRunning = Object.values(timers).some((row) => row?.running);
    if (!anyRunning) return undefined;
    const id = window.setInterval(() => {
      broadcastRef.current(timersRef.current);
    }, 2000);
    return () => window.clearInterval(id);
  }, [open, timers]);

  const associateUploadedVideo = useCallback(
    async (uploaded, snapshot) => {
      if (!uploaded?.url) return;
      for (const rowPlayer of playersRef.current || []) {
        const row = snapshot?.[rowPlayer.key];
        if (!row || row.running || row.videoStartMs == null) continue;
        // Only segments filmed during this recording belong to this clip.
        if (!uploaded.sessionId || row.videoSessionId !== uploaded.sessionId) {
          continue;
        }
        const timeSec =
          row.savedSec != null ? row.savedSec : Number(row.displaySec) || 0;
        try {
          const data = await persistRecord(rowPlayer, timeSec, {
            videoUrl: uploaded.url,
            videoMessageId: uploaded.messageId,
            videoStartMs: row.videoStartMs,
            videoEndMs: row.videoEndMs,
            startedAtWall: row.startedAtWall,
            stoppedAtWall: row.stoppedAtWall,
            videoEpochAt: uploaded.videoEpochAt || row.videoEpochAt,
          });
          const video = data?.video || null;
          setTimers((prev) => {
            const next = {
              ...prev,
              [rowPlayer.key]: applyVideoMetaFromRecord(
                {
                  ...(prev[rowPlayer.key] || emptyTimerState()),
                  dirty: false,
                  savedSec: timeSec,
                  running: false,
                },
                video || {
                  url: uploaded.url,
                  message_id: uploaded.messageId,
                  start_ms: row.videoStartMs,
                  end_ms: row.videoEndMs,
                  video_epoch_at: uploaded.videoEpochAt,
                }
              ),
            };
            broadcastRef.current(next, false);
            return next;
          });
        } catch {
          /* coach can save again */
        }
      }
    },
    [persistRecord]
  );

  const handleStart = useCallback(
    async (key) => {
      const cur = timersRef.current[key] || emptyTimerState();
      if (cur.running) return;
      // A previous clip may still be uploading; this starts (or joins) a new one.
      let started = null;
      try {
        started = await sessionVideo.startRecording();
      } catch {
        started = null;
      }
      const recordingNow = Boolean(started?.sessionId);
      const offset = recordingNow ? sessionVideo.markOffsetMs() ?? 0 : null;
      const wall = new Date().toISOString();
      const startedAt = Date.now();
      setTimers((prev) => {
        const row = prev[key] || emptyTimerState();
        if (row.running) return prev;
        const next = {
          ...prev,
          [key]: {
            ...row,
            running: true,
            startedAt,
            error: null,
            dirty: true,
            videoStartMs: offset,
            videoEndMs: null,
            videoUrl: recordingNow ? null : row.videoUrl,
            videoMessageId: recordingNow ? null : row.videoMessageId,
            videoSessionId: recordingNow ? started.sessionId : null,
            startedAtWall: wall,
            stoppedAtWall: null,
            videoEpochAt: recordingNow ? started.epochAt : row.videoEpochAt,
          },
        };
        broadcastRef.current(next);
        return next;
      });
    },
    [sessionVideo]
  );

  /** Stop the clock, auto-save the time, stamp the clip, upload when idle. */
  const handleStop = useCallback(
    (player) => {
      const key = player.key;
      const cur = timersRef.current[key] || emptyTimerState();
      if (!cur.running || cur.saving) return;
      const now = Date.now();
      const finalSec =
        cur.startedAt != null
          ? (cur.baseMs + (now - cur.startedAt)) / 1000
          : Number(cur.displaySec) || 0;
      const inCurrentClip =
        sessionVideo.recording &&
        Boolean(cur.videoSessionId) &&
        cur.videoSessionId === sessionVideo.sessionId;
      const videoEndMs = inCurrentClip
        ? sessionVideo.markOffsetMs()
        : cur.videoEndMs;
      const stoppedAtWall = new Date(now).toISOString();
      const nextRow = {
        ...(timersRef.current[key] || emptyTimerState()),
        running: false,
        startedAt: null,
        baseMs: Math.round(finalSec * 1000),
        displaySec: finalSec,
        dirty: true,
        saving: true,
        error: null,
        videoEndMs,
        stoppedAtWall,
        videoEpochAt:
          (inCurrentClip && sessionVideo.epochAt) || cur.videoEpochAt,
      };
      const snapshot = { ...timersRef.current, [key]: nextRow };
      setTimers(snapshot);
      broadcastRef.current(snapshot);

      void (async () => {
        try {
          // "" clears the previous clip on the server until this one uploads.
          const awaitingClip = Boolean(nextRow.videoSessionId) && !nextRow.videoUrl;
          const data = await persistRecord(player, finalSec, {
            videoUrl: awaitingClip ? "" : nextRow.videoUrl,
            videoMessageId: awaitingClip ? "" : nextRow.videoMessageId,
            videoStartMs: nextRow.videoStartMs,
            videoEndMs: nextRow.videoEndMs,
            startedAtWall: nextRow.startedAtWall,
            stoppedAtWall: nextRow.stoppedAtWall,
            videoEpochAt: nextRow.videoEpochAt,
          });
          const saved = Number(data?.metric_value);
          const video = data?.video || null;
          setTimers((prev) => {
            const next = {
              ...prev,
              [key]: applyVideoMetaFromRecord(
                {
                  ...(prev[key] || emptyTimerState()),
                  saving: false,
                  dirty: false,
                  savedSec: Number.isFinite(saved) ? saved : finalSec,
                  displaySec: Number.isFinite(saved) ? saved : finalSec,
                  baseMs: Math.round(
                    (Number.isFinite(saved) ? saved : finalSec) * 1000
                  ),
                  running: false,
                  startedAt: null,
                },
                video
              ),
            };
            broadcastRef.current(next);
            return next;
          });
        } catch (err) {
          setTimers((prev) => ({
            ...prev,
            [key]: {
              ...(prev[key] || emptyTimerState()),
              saving: false,
              dirty: true,
              error: err?.message || t("error") || "Error",
            },
          }));
        }
      })();

      const stillRunning = Object.values(snapshot).some((row) => row?.running);
      if (stillRunning || !sessionVideo.recording) return;
      const anchor =
        (playersRef.current || []).find((p) => p.key === key) || player;
      void (async () => {
        try {
          const uploaded = await sessionVideo.stopAndUpload({
            playerId: anchor?.playerId || null,
          });
          await associateUploadedVideo(uploaded, timersRef.current);
        } catch {
          /* sessionVideo.error */
        }
      })();
    },
    [sessionVideo, persistRecord, associateUploadedVideo, t]
  );

  const handleSave = useCallback(
    async (player) => {
      const key = player.key;
      const cur = timersRef.current[key] || emptyTimerState();
      if (cur.saving || cur.running) return;
      const timeSec = Number(cur.displaySec) || 0;
      setTimers((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || emptyTimerState()),
          saving: true,
          error: null,
        },
      }));
      try {
        const data = await persistRecord(player, timeSec, {
          videoUrl: cur.videoUrl,
          videoMessageId: cur.videoMessageId,
          videoStartMs: cur.videoStartMs,
          videoEndMs: cur.videoEndMs,
          startedAtWall: cur.startedAtWall,
          stoppedAtWall: cur.stoppedAtWall,
          videoEpochAt: cur.videoEpochAt,
        });
        const saved = Number(data?.metric_value);
        const video = data?.video || null;
        setTimers((prev) => ({
          ...prev,
          [key]: applyVideoMetaFromRecord(
            {
              ...(prev[key] || emptyTimerState()),
              saving: false,
              dirty: false,
              savedSec: Number.isFinite(saved) ? saved : timeSec,
              displaySec: Number.isFinite(saved) ? saved : timeSec,
              baseMs: Math.round(
                (Number.isFinite(saved) ? saved : timeSec) * 1000
              ),
              running: false,
              startedAt: null,
            },
            video
          ),
        }));
      } catch (err) {
        setTimers((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || emptyTimerState()),
            saving: false,
            error: err?.message || t("error") || "Error",
          },
        }));
      }
    },
    [persistRecord, t]
  );

  const handleReset = useCallback((key) => {
    setTimers((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || emptyTimerState()),
        running: false,
        startedAt: null,
        baseMs: 0,
        displaySec: 0,
        dirty: prev[key]?.savedSec != null,
        error: null,
        videoStartMs: null,
        videoEndMs: null,
        videoSessionId: null,
        startedAtWall: null,
        stoppedAtWall: null,
      },
    }));
  }, []);

  const handleDelete = useCallback(
    async (player) => {
      const key = player.key;
      setTimers((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || emptyTimerState()),
          deleting: true,
          error: null,
        },
      }));
      try {
        await deleteTeChronoRecord(teId, {
          userId: player.userId,
          playerId: player.playerId,
        });
        setTimers((prev) => ({
          ...prev,
          [key]: emptyTimerState(),
        }));
      } catch (err) {
        setTimers((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || emptyTimerState()),
            deleting: false,
            error: err?.message || t("error") || "Error",
          },
        }));
      }
    },
    [teId, t]
  );

  const handleStartVideo = useCallback(async () => {
    try {
      await sessionVideo.startRecording();
    } catch {
      /* error surfaced on sessionVideo.error */
    }
  }, [sessionVideo]);

  const handleStopVideo = useCallback(async () => {
    const firstPlayer = (playersRef.current || []).find((p) => p.playerId);
    try {
      const uploaded = await sessionVideo.stopAndUpload({
        playerId: firstPlayer?.playerId || null,
      });
      await associateUploadedVideo(uploaded, timersRef.current);
    } catch {
      /* sessionVideo.error */
    }
  }, [sessionVideo, associateUploadedVideo]);

  const handleReview = useCallback((player) => {
    const row = timersRef.current[player.key] || emptyTimerState();
    const url = row.videoUrl;
    if (!url) return;
    setReviewClip({
      url,
      messageId: row.videoMessageId,
      startSec:
        row.videoStartMs != null ? Math.max(0, row.videoStartMs / 1000) : 0,
      endSec:
        row.videoEndMs != null
          ? Math.max(0, row.videoEndMs / 1000)
          : null,
      playerName: player.name,
    });
  }, []);

  const flushVideoOnExit = useCallback(async () => {
    if (!sessionVideo.recording) return;
    const snapshot = timersRef.current;
    const hasSegments = Object.values(snapshot).some(
      (row) =>
        row?.videoStartMs != null &&
        row.videoSessionId === sessionVideo.sessionId
    );
    if (!hasSegments) {
      sessionVideo.discard();
      return;
    }
    const firstPlayer = (playersRef.current || []).find((p) => p.playerId);
    try {
      const uploaded = await sessionVideo.stopAndUpload({
        playerId: firstPlayer?.playerId || null,
      });
      await associateUploadedVideo(uploaded, timersRef.current);
    } catch {
      sessionVideo.discard();
    }
  }, [sessionVideo, associateUploadedVideo]);

  const handleClose = useCallback(() => {
    setReviewClip(null);
    onClose?.();
  }, [onClose]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return undefined;
    }
    if (!wasOpenRef.current) return undefined;
    wasOpenRef.current = false;
    void flushVideoOnExit();
    return undefined;
  }, [open, flushVideoOnExit]);

  const toggleAllCameras = () => {
    selectionTouchedRef.current = true;
    setSelectAllCameras((prev) => {
      const next = !prev;
      setSelectedCameraKeys(
        next
          ? new Set(
              cameraOptions
                .map((opt) => stationCameraTargetKey(opt))
                .filter(Boolean)
            )
          : new Set()
      );
      return next;
    });
  };

  const toggleCameraKey = (key) => {
    selectionTouchedRef.current = true;
    setSelectAllCameras(false);
    setSelectedCameraKeys((prev) => {
      const base = selectAllCameras
        ? new Set(
            cameraOptions
              .map((opt) => stationCameraTargetKey(opt))
              .filter(Boolean)
          )
        : new Set(prev);
      if (base.has(key)) base.delete(key);
      else base.add(key);
      return base;
    });
  };

  if (!open || typeof document === "undefined") return null;

  const portalRoot = getLiveTrainingPortalRoot() || document.body;
  const recordedCount = (players || []).filter(
    (p) => timers[p.key]?.savedSec != null
  ).length;
  const linkedCamera =
    normalizeStationCameraTarget(sessionVideo.cameraTarget) ||
    getStationCameraTarget(stationKey);
  const singleSelected =
    !selectAllCameras && selectedCameraTargets.length === 1
      ? selectedCameraTargets[0]
      : null;
  const stationBound =
    Boolean(stationKey) &&
    Boolean(singleSelected) &&
    stationCameraTargetKey(linkedCamera) ===
      stationCameraTargetKey(singleSelected);
  const infoLabel = t("liveTrainingChronoInfo", "Information");
  const modeHelp =
    t("liveTrainingChronoModeSubtitle") ||
    "Start and stop each player’s chronometer, then Save to store the time and video segment.";
  const cameraHelp =
    t("liveTrainingChronoVideoHelp") ||
    "Select one camera, several, or all. A second phone signed in as player on this account is listed. The coach phone only starts and stops times — it never records.";
  const captureAllHelp =
    t("liveTrainingChronoCaptureAllHint") ||
    "Record starts every table, station, and player camera phone connected to this session.";

  const sheet = (
    <div
      className="lt-chrono-panel__backdrop"
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="lt-chrono-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lt-chrono-panel-title"
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lt-chrono-panel__header">
          <div className="lt-chrono-panel__header-text">
            <p className="lt-chrono-panel__eyebrow">
              {t("liveTrainingChronoModeEyebrow") || "Off-table"}
            </p>
            <h2 id="lt-chrono-panel-title">
              <TimerOutlinedIcon fontSize="small" />
              <span>
                {t("liveTrainingChronoModeTitle") || "Record chrono mode"}
              </span>
              <ChronoInfoButton
                id="mode"
                text={modeHelp}
                openId={infoOpenId}
                onToggle={setInfoOpenId}
                label={infoLabel}
              />
            </h2>
          </div>
          <button
            type="button"
            className="lt-chrono-panel__close"
            onClick={handleClose}
            aria-label={t("close") || "Close"}
          >
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="lt-chrono-panel__body">
        <div className="lt-chrono-panel__video-bar">
          {showPlayerPresenceShortcut ? (
            <button
              type="button"
              className="lt-chrono-panel__btn lt-chrono-panel__btn--add-players"
              onClick={() => onOpenPlayerPresence?.()}
              disabled={playerPresenceDisabled || !onOpenPlayerPresence}
              title={
                playerPresenceDisabled
                  ? playerPresenceDisabledTitle ||
                    t("managePlayerPresence", "Manage player presence")
                  : t("managePlayerPresence", "Add / manage players")
              }
              aria-label={t("managePlayerPresence", "Manage player presence")}
            >
              <PersonAddIcon fontSize="small" />
              <span>
                {t("addPlayer", "Add player")}
              </span>
            </button>
          ) : null}
          {!sessionVideo.recording ? (
            <button
              type="button"
              className="lt-chrono-panel__btn lt-chrono-panel__btn--video"
              onClick={handleStartVideo}
            >
              <VideocamIcon fontSize="small" />
              {captureAllCameras || selectAllCameras
                ? t(
                    "liveTrainingChronoVideoStartAll",
                    "Record all cameras"
                  )
                : t("liveTrainingChronoVideoStart") || "Record video"}
            </button>
          ) : (
            <button
              type="button"
              className="lt-chrono-panel__btn lt-chrono-panel__btn--video-stop"
              onClick={handleStopVideo}
            >
              <VideocamOffIcon fontSize="small" />
              {t("liveTrainingChronoVideoStop") || "Stop & upload video"}
              {` · ${formatChronoClock(sessionVideo.elapsedSec)}`}
            </button>
          )}
          {sessionVideo.uploading ? (
            <span className="lt-chrono-panel__video-hint">
              {t("liveTrainingChronoVideoUploading") || "Uploading video…"}
            </span>
          ) : null}
          {sessionVideo.videoUrl && !sessionVideo.recording ? (
            <span className="lt-chrono-panel__video-hint lt-chrono-panel__video-hint--ok">
              {t("liveTrainingChronoVideoReady") || "Video linked to segments"}
            </span>
          ) : null}
          {sessionVideo.error ? (
            <span className="lt-chrono-panel__video-hint lt-chrono-panel__video-hint--err" role="alert">
              {sessionVideo.error}
            </span>
          ) : null}
          {captureAllCameras ? (
            <ChronoInfoButton
              id="capture-all"
              text={captureAllHelp}
              openId={infoOpenId}
              onToggle={setInfoOpenId}
              label={infoLabel}
            />
          ) : null}
        </div>

        {stationKey && !captureAllCameras ? (
          <div className="lt-chrono-panel__camera-bind">
            <div className="lt-chrono-panel__camera-label">
              <span className="lt-chrono-panel__section-label">
                {t("liveTrainingChronoStationCamera") ||
                  "Camera phone for this station"}
                <ChronoInfoButton
                  id="cameras"
                  text={cameraHelp}
                  openId={infoOpenId}
                  onToggle={setInfoOpenId}
                  label={infoLabel}
                />
              </span>
              <div
                className="lt-chrono-panel__camera-list"
                role="group"
                aria-label={
                  t("liveTrainingChronoStationCamera") ||
                  "Camera phone for this station"
                }
              >
                <label className="lt-chrono-panel__camera-option lt-chrono-panel__camera-option--all">
                  <input
                    type="checkbox"
                    checked={selectAllCameras}
                    disabled={sessionVideo.recording}
                    onChange={toggleAllCameras}
                  />
                  <span>
                    {t("liveTrainingChronoAllCameras", "All cameras")}
                  </span>
                </label>
                {cameraOptions.length ? (
                  cameraOptions.map((opt) => {
                    const key = stationCameraTargetKey(opt);
                    const checked =
                      selectAllCameras || selectedCameraKeys.has(key);
                    return (
                      <label key={key} className="lt-chrono-panel__camera-option">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={sessionVideo.recording}
                          onChange={() => toggleCameraKey(key)}
                        />
                        <span>
                          {opt.label ||
                            stationCameraTargetLabel(opt, t, { stationName })}
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <p className="lt-chrono-panel__camera-empty">
                    {t(
                      "liveTrainingChronoNoCameras",
                      "No camera phones connected yet."
                    )}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              className={`lt-chrono-panel__btn lt-chrono-panel__btn--link-cam${
                stationBound ? " lt-chrono-panel__btn--link-cam-active" : ""
              }`}
              onClick={() => {
                if (!singleSelected) return;
                setStationCameraTarget(stationKey, singleSelected);
                sessionVideo.setCameraTarget(singleSelected, {
                  bindToStation: true,
                });
              }}
              disabled={
                !singleSelected ||
                sessionVideo.recording ||
                sessionVideo.uploading
              }
              title={
                t("liveTrainingChronoBindCamera") ||
                "Remember this camera phone for the station"
              }
            >
              <LinkIcon fontSize="small" />
              {stationBound
                ? t("liveTrainingChronoCameraLinked") || "Linked"
                : t("liveTrainingChronoBindCamera") || "Link to station"}
            </button>
          </div>
        ) : null}

        {recordedCount > 0 ? (
          <p className="lt-chrono-panel__summary">
            {t("liveTrainingChronoRecordedCount", { count: recordedCount }) ||
              `${recordedCount} recorded time(s)`}
          </p>
        ) : null}

        {loadError ? (
          <p className="lt-chrono-panel__error" role="alert">
            {loadError}
          </p>
        ) : null}
        {loading ? (
          <p className="lt-chrono-panel__hint">{t("loading") || "…"}</p>
        ) : null}

        {!players.length ? (
          <div className="lt-chrono-panel__empty-wrap">
            <p className="lt-chrono-panel__empty">
              {t("liveTrainingChronoModeNoPlayers") ||
                "No present players in this group yet. Mark attendance first."}
            </p>
            {showPlayerPresenceShortcut && onOpenPlayerPresence ? (
              <button
                type="button"
                className="lt-chrono-panel__btn lt-chrono-panel__btn--add-players"
                onClick={() => onOpenPlayerPresence()}
                disabled={playerPresenceDisabled}
              >
                <PersonAddIcon fontSize="small" />
                {t("addPlayer", "Add player")}
              </button>
            ) : null}
          </div>
        ) : (
          <ol className="lt-chrono-panel__list">
            {players.map((player, index) => {
              const row = timers[player.key] || emptyTimerState();
              const hasSegment =
                row.videoStartMs != null || row.videoEndMs != null;
              const canReview = Boolean(row.videoUrl && hasSegment);
              const isRecordingPlayer =
                row.running &&
                Boolean(sessionVideo.recording) &&
                Boolean(row.videoSessionId) &&
                row.videoSessionId === sessionVideo.sessionId;
              const canSave =
                !row.running &&
                !row.saving &&
                !row.deleting &&
                (row.dirty ||
                  (row.displaySec > 0 && row.savedSec == null));
              return (
                <li
                  key={player.key}
                  className={`lt-chrono-panel__row${
                    row.running ? " lt-chrono-panel__row--running" : ""
                  }${isRecordingPlayer ? " lt-chrono-panel__row--rec" : ""}`}
                >
                  <div className="lt-chrono-panel__identity">
                    <span className="lt-chrono-panel__order">{index + 1}</span>
                    <div>
                      <div className="lt-chrono-panel__name">
                        <span className="lt-chrono-panel__name-text">
                          {player.name}
                        </span>
                        {isRecordingPlayer ? (
                          <span className="lt-chrono-panel__rec" aria-live="polite">
                            REC
                          </span>
                        ) : null}
                      </div>
                      {player.personalizedExerciseName ? (
                        <div className="lt-chrono-panel__drill">
                          {player.personalizedExerciseName}
                        </div>
                      ) : null}
                      <div className="lt-chrono-panel__meta">
                        {player.tableNumber != null
                          ? `${t("liveTrainingChronoTable") || "Table"} ${player.tableNumber}`
                          : t("liveTrainingChronoPresent") || "Present"}
                        {row.savedSec != null
                          ? ` · ${t("liveTrainingChronoSaved") || "Saved"} ${formatChronoClock(row.savedSec)}`
                          : null}
                        {row.dirty && !row.running
                          ? ` · ${t("liveTrainingChronoUnsaved") || "Unsaved"}`
                          : null}
                        {hasSegment
                          ? ` · ${t("liveTrainingChronoSegment") || "Segment"} ${formatChronoClock((row.videoStartMs || 0) / 1000)}–${row.videoEndMs != null ? formatChronoClock(row.videoEndMs / 1000) : "…"}`
                          : null}
                        {row.videoUrl
                          ? ` · ${t("liveTrainingChronoVideoLinked") || "Video"}`
                          : null}
                      </div>
                    </div>
                  </div>
                  <ChronoClockDisplay
                    running={row.running}
                    startedAt={row.startedAt}
                    baseMs={row.baseMs}
                    displaySec={row.displaySec}
                  />
                  <div className="lt-chrono-panel__actions">
                    {!row.running ? (
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--start"
                        onClick={() => handleStart(player.key)}
                        disabled={row.saving || row.deleting}
                      >
                        <PlayArrowIcon fontSize="small" />
                        {t("liveTrainingChronoStart") || "Start"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--stop"
                        onClick={() => handleStop(player)}
                        disabled={row.saving || row.deleting}
                      >
                        <StopIcon fontSize="small" />
                        {t("liveTrainingChronoStop") || "Stop"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-chrono-panel__btn--start lt-chrono-panel__btn--save"
                      onClick={() => handleSave(player)}
                      disabled={!canSave}
                      title={t("liveTrainingChronoSave") || "Save time & segment"}
                    >
                      <SaveOutlinedIcon fontSize="small" />
                      {t("save") || "Save"}
                    </button>
                    {canReview ? (
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--review"
                        onClick={() => handleReview(player)}
                        disabled={row.saving || row.deleting}
                        title={
                          t("liveTrainingChronoReview") ||
                          "Review performance segment"
                        }
                      >
                        <MovieOutlinedIcon fontSize="small" />
                        {t("liveTrainingChronoReview") || "Review"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="lt-chrono-panel__btn lt-chrono-panel__btn--reset"
                      onClick={() => handleReset(player.key)}
                      disabled={row.running || row.saving || row.deleting}
                      title={t("liveTrainingChronoReset") || "Reset"}
                      aria-label={t("liveTrainingChronoReset") || "Reset"}
                    >
                      <RestartAltIcon fontSize="small" />
                    </button>
                    {row.savedSec != null ? (
                      <button
                        type="button"
                        className="lt-chrono-panel__btn lt-chrono-panel__btn--delete"
                        onClick={() => handleDelete(player)}
                        disabled={row.running || row.saving || row.deleting}
                        title={t("liveTrainingChronoDelete") || "Delete time"}
                        aria-label={t("liveTrainingChronoDelete") || "Delete time"}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    ) : null}
                  </div>
                  {row.error ? (
                    <p className="lt-chrono-panel__row-error" role="alert">
                      {row.error}
                    </p>
                  ) : null}
                  {row.saving ? (
                    <p className="lt-chrono-panel__row-hint">
                      {t("saving") || "Saving…"}
                    </p>
                  ) : null}
                  {row.deleting ? (
                    <p className="lt-chrono-panel__row-hint">
                      {t("deleting") || "Deleting…"}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(sheet, portalRoot)}
      {reviewClip && reviewUrl ? (
        <ReviewVideoFullscreenPlayer
          url={reviewUrl}
          onClose={() => setReviewClip(null)}
          title={
            reviewClip.playerName
              ? `${t("liveTrainingChronoReview") || "Review"} — ${reviewClip.playerName}`
              : t("liveTrainingChronoReview") || "Review"
          }
          autoPlay
          enablePose={false}
          enableJointOverlay
          isEvaluationVideo={false}
          messageId={reviewClip.messageId || null}
          teId={teId}
          initialCurrentTime={reviewClip.startSec}
          playbackWindowStart={reviewClip.startSec}
          playbackWindowEnd={reviewClip.endSec}
        />
      ) : null}
    </>
  );
}

export default LiveTrainingCoachChronoPanel;
